import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { createContext, useContext } from "react";

interface SortableContextValue {
  attributes: Record<string, any>;
  listeners: Record<string, any> | undefined;
}

const SortableHandleContext = createContext<SortableContextValue | null>(null);

export function useDragHandle() {
  const ctx = useContext(SortableHandleContext);
  if (!ctx) throw new Error("useDragHandle must be used within SortableItem");
  return ctx;
}

interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

export function SortableItem({ id, children, className }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={cn("relative", className)}>
      <SortableHandleContext.Provider value={{ attributes, listeners }}>
        {children}
      </SortableHandleContext.Provider>
    </div>
  );
}

export function DragHandle({ className }: { className?: string }) {
  const { attributes, listeners } = useDragHandle();
  return (
    <button
      type="button"
      className={cn(
        "cursor-grab active:cursor-grabbing touch-none p-1 text-muted-foreground hover:text-foreground shrink-0",
        className
      )}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}
